import { NAV_ITEMS } from "../../constants/nav-items.constant";
import { MenuNameOverrideMap, ParentLeafLinkMap, RoleAccessMap, RoleSubAccessMap, SubMenuNameOverrideMap } from "../../constants/role-access-map.constant";
import { MenuItem } from "../../interfaces/menu-item.interface";

// export function getMenuItemsByRole(role: string, permissions: string[] = []): MenuItem[] {
//   const allowedOrder = RoleAccessMap[role] ?? [];
//   const subRules = RoleSubAccessMap[role] ?? {};
//   const nameOverrides = MenuNameOverrideMap[role] ?? {};
//   const leafLinkOverrides = ParentLeafLinkMap[role] ?? {};

//    // Helper function to check if user has the required permission for a menu item
//   const hasPermission = (item: MenuItem): boolean => {
//     if (!item.permission || item.permission.length === 0) return true; // No permission needed
//     return item.permission.some(p => permissions.includes(p)); // Check if user has any of the required permissions
//   };
//   const buildItem = (key: string): MenuItem | null => {
//     const original = NAV_ITEMS.find(i => i.key === key);
//     if (!original) return null;

//     const rule = subRules[key]; // undefined | string[]
//     let link = original.link;

//      // Filter submenus based on permissions
//     let submenu = original.submenu ? original.submenu.filter(s => hasPermission(s)) : null;

//     // If the user doesn't have permission for the parent or any of its submenus, don't display the item
//     if (!hasPermission(original) && (!submenu || submenu.length === 0)) return null;

//      // If no submenu to process, return early if the user doesn't have permission for this menu item
//     if (!submenu) {
//       if (!hasPermission(original)) {
//         return null; // Skip if the user doesn't have permission
//       }
//       return {
//         ...original,
//         name: nameOverrides[key] ?? original.name,
//         link,
//         submenu: null,
//       };
//     }

//       // If no specific rule for this parent, keep submenu as-is
//     if (!Array.isArray(rule)) {
//       if (hasPermission(original)) {
//         return {
//           ...original,
//           name: nameOverrides[key] ?? original.name,
//           link,
//           submenu,
//         };
//       }
//       return null; // Don't show the item if the user doesn't have permission
//     }


//    // rule is an array: empty => show parent only, non-empty => filter children
//     if (rule.length === 0) {
//       submenu = null;
//       if ((!link || link === '#' || link === '' || link === "/") && leafLinkOverrides[key]) {
//         link = leafLinkOverrides[key];
//       }
//     } else {
//       const allowedChildren = new Set(rule);

//      // Filter submenu items based on whether the user has permission for the submenu's key
//       submenu = submenu.filter(s => {
//         // Check role allows this child and the user has permission for it
//         return allowedChildren.has(s.key) && hasPermission(s);
//       });

//       // If no allowed submenus remain, set submenu to null
//       submenu = submenu.length === 0 ? null : submenu;
//     }

//    // Return the item if it has submenu or if the user has permission for the parent key
//     if (submenu || hasPermission(original)) {
//       return {
//         ...original,
//         name: nameOverrides[key] ?? original.name,
//         link,
//         submenu,
//       };
//     }

//     return null;
//   };

//   return allowedOrder
//     .map(buildItem)
//     .filter((x): x is MenuItem => !!x);
// }
export function getMenuItemsByRole(
  role: string,
  permissions: string[] = [],
): MenuItem[] {
  const allowedOrder = RoleAccessMap[role] ?? [];
  const subRules = RoleSubAccessMap[role] ?? {};
  const nameOverrides = MenuNameOverrideMap[role] ?? {};
  const submenuNameOverrides = SubMenuNameOverrideMap[role] ?? {};
  const leafLinkOverrides = ParentLeafLinkMap[role] ?? {};

  const hasPermission = (item: MenuItem): boolean => {
    if (!item.permission?.length) {
      return true;
    }

    return item.permission.some(permission =>
      permissions.includes(permission),
    );
  };

  const applySubmenuNameOverrides = (
    parentKey: string,
    submenu: MenuItem[],
  ): MenuItem[] => {
    const overrides = submenuNameOverrides[parentKey] ?? {};

    return submenu.map(item => ({
      ...item,
      name: overrides[item.key] ?? item.name,
    }));
  };

  const buildItem = (key: string): MenuItem | null => {
    const original = NAV_ITEMS.find(item => item.key === key);

    if (!original) {
      return null;
    }

    const rule = subRules[key];
    let link = original.link;

    let submenu = original.submenu
      ? original.submenu.filter(item => hasPermission(item))
      : null;

    // Hide the entire item when neither the parent nor any child is allowed.
    if (
      !hasPermission(original) &&
      (!submenu || submenu.length === 0)
    ) {
      return null;
    }

    // The menu is already a leaf.
    if (!submenu) {
      if (!hasPermission(original)) {
        return null;
      }

      return {
        ...original,
        name: nameOverrides[key] ?? original.name,
        link,
        submenu: null,
      };
    }

    // No role-specific submenu restriction.
    if (!Array.isArray(rule)) {
      if (!hasPermission(original)) {
        return null;
      }

      return {
        ...original,
        name: nameOverrides[key] ?? original.name,
        link,
        submenu: applySubmenuNameOverrides(key, submenu),
      };
    }

    // Empty rule converts the parent into a leaf.
    if (rule.length === 0) {
      submenu = null;

      if (
        (!link || link === '#' || link === '' || link === '/') &&
        leafLinkOverrides[key]
      ) {
        link = leafLinkOverrides[key];
      }
    } else {
      const allowedChildren = new Set(rule);

      submenu = submenu.filter(item =>
        allowedChildren.has(item.key),
      );

      if (submenu.length > 0) {
        submenu = applySubmenuNameOverrides(key, submenu);
      } else {
        submenu = null;
      }
    }

    if (!submenu && !hasPermission(original)) {
      return null;
    }

    return {
      ...original,
      name: nameOverrides[key] ?? original.name,
      link,
      submenu,
    };
  };

  return allowedOrder
    .map(buildItem)
    .filter((item): item is MenuItem => item !== null);
}